import { PasskeySettings } from '../../PasskeySettings'
import { useState, useEffect } from 'react'
import { LogOut, User } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button, cn, Input, Label, Switch } from '@valiant/ui-shared'
import { toast } from 'sonner'
import {
  useGetMeQuery,
  useUpdateMeMutation,
  useResetMyPasswordMutation,
  useLogoutMutation,
} from '../../../generated/graphql'
import { useAuth } from '../../../AuthProvider'

export function ProfileContent({
  className,
  onOpenChange,
  onResetPasswordRequest,
}: {
  className?: string
  onOpenChange?: (open: boolean) => void
  onResetPasswordRequest?: (confirmFn: () => Promise<void>) => void
}) {
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [emailNotifications, setEmailNotifications] = useState(false)
  const [isLoading, setIsLoading] = useState({
    username: false,
    email: false,
    password: false,
  })
  const [originalValues, setOriginalValues] = useState({
    username: '',
    email: '',
    emailNotifications: false,
  })
  const auth = useAuth()

  // GraphQL hooks
  const [{ data: userData, fetching: _userLoading }] = useGetMeQuery()
  const [, updateMe] = useUpdateMeMutation()
  const [, resetPassword] = useResetMyPasswordMutation()
  const [, logoutMutation] = useLogoutMutation()

  // Update local state when user data loads
  useEffect(() => {
    if (userData?.getMe) {
      const user = userData.getMe
      setUsername(user.username || '')
      setEmail(user.email || '')
      setEmailNotifications(user.emailNotifications ?? false)
      setOriginalValues({
        username: user.username || '',
        email: user.email || '',
        emailNotifications: user.emailNotifications ?? false,
      })
    }
  }, [userData])

  const handleSaveUsername = async () => {
    if (!username.trim()) {
      toast.error('Username cannot be empty')
      return
    }

    setIsLoading((prev) => ({ ...prev, username: true }))
    try {
      const result = await updateMe({
        input: {
          username,
          emailNotifications,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      // Update original values after successful save
      setOriginalValues((prev) => ({ ...prev, username }))
      toast.success('Username updated successfully')
    } catch (error) {
      toast.error('Failed to update username', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    } finally {
      setIsLoading((prev) => ({ ...prev, username: false }))
    }
  }

  const handleSaveEmail = async () => {
    if (!email.trim() || !isValidEmail(email)) {
      toast.error('Please enter a valid email address')
      return
    }

    setIsLoading((prev) => ({ ...prev, email: true }))
    try {
      const result = await updateMe({
        input: {
          email,
          emailNotifications,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      // Update original values after successful save
      setOriginalValues((prev) => ({ ...prev, email }))
      toast.success('Email updated successfully')
    } catch (error) {
      toast.error('Failed to update email', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    } finally {
      setIsLoading((prev) => ({ ...prev, email: false }))
    }
  }

  const handleResetPassword = () => {
    const confirmFn = async () => {
      setIsLoading((prev) => ({ ...prev, password: true }))
      try {
        const result = await resetPassword({})
        if (result.error) {
          throw new Error(result.error.message)
        }
        toast.success('Password reset email sent')
      } catch (error) {
        toast.error('Failed to reset password', {
          description: error instanceof Error ? error.message : 'An unexpected error occurred',
        })
      } finally {
        setIsLoading((prev) => ({ ...prev, password: false }))
      }
    }
    onResetPasswordRequest?.(confirmFn)
  }

  const handleViewLogs = () => {
    if (userData?.getMe?.id) {
      navigate(`/logs/${userData.getMe.id}`)
      onOpenChange?.(false)
    }
  }

  const handleLogout = async () => {
    try {
      const result = await logoutMutation({})

      if (result.error) {
        auth.signout()
        throw new Error(result.error.message)
      }

      toast.success('Logged out successfully')
      auth.signout()
      onOpenChange?.(false)
    } catch (error) {
      toast.error('Failed to logout', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    }
  }

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Check if inputs have changed from original values
  const hasUsernameChanged = username !== originalValues.username
  const hasEmailChanged = email !== originalValues.email

  return (
    <div className={cn('flex flex-col h-full p-5', className)}>
      {/* Profile Header */}
      <div className="flex items-center gap-2 pb-3 mb-4">
        <User className="w-5 h-5 text-black " />

        <h3 className="font-semibold text-2xl">Profile</h3>
      </div>

      {/* Content Section */}
      <div className="flex-1 space-y-4">
        {/* Username Field */}
        <div className="space-y-2">
          <Label htmlFor="username" className="text-card-text">
            Username
          </Label>
          <div className="flex gap-2 relative items-center">
            <Input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="flex-1 pr-22"
              disabled={isLoading.username}
            />
            <Button
              onClick={handleSaveUsername}
              variant="main_action"
              className="rounded-md absolute right-4"
              disabled={isLoading.username || !hasUsernameChanged}
            >
              {isLoading.username ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Email Field */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-card-text">
            Email
          </Label>
          <div className="flex gap-2 relative items-center">
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 pr-22"
              disabled={isLoading.email}
            />
            <Button
              onClick={handleSaveEmail}
              variant="main_action"
              className="rounded-md absolute right-4"
              disabled={isLoading.email || !hasEmailChanged}
            >
              {isLoading.email ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Password Reset */}
        <div className="space-y-2">
          <Label className="text-card-text">Password</Label>
          <div className="w-full px-4 mt-4">
            <Button
              onClick={handleResetPassword}
              variant="main_action"
              className="w-full justify-center rounded-md"
              disabled={isLoading.password}
            >
              {isLoading.password ? 'Sending reset email...' : 'Reset Password'}
            </Button>
          </div>
        </div>

        {/* Email Notifications */}
        <div className="flex flex-col justify-between py-2">
          <Label htmlFor="notifications" className="text-card-text">
            Email Notifications
          </Label>
          <div className="w-full px-4 mt-4 border border-card-border rounded-md p-4 flex items-center justify-between">
            {emailNotifications ? 'On' : 'Off'}
            <Switch
              id="notifications"
              checked={emailNotifications}
              onCheckedChange={async (checked) => {
                const previousValue = emailNotifications
                setEmailNotifications(checked)

                try {
                  const result = await updateMe({
                    input: {
                      emailNotifications: checked,
                    },
                  })

                  if (result.error) {
                    setEmailNotifications(previousValue)
                    throw new Error(result.error.message)
                  }

                  // Update original values after successful save
                  setOriginalValues((prev) => ({ ...prev, emailNotifications: checked }))
                  toast.success('Email notifications updated')
                } catch (error) {
                  setEmailNotifications(previousValue)
                  toast.error('Failed to update notifications', {
                    description:
                      error instanceof Error ? error.message : 'An unexpected error occurred',
                  })
                }
              }}
            />
          </div>
        </div>

        {/* Logs Section */}
        <div className="space-y-2">
          <Label className="text-card-text">Logs</Label>

          <div className="w-full px-4 mt-4">
            <Button
              onClick={handleViewLogs}
              variant="main_action"
              className="w-full justify-center rounded-md"
              disabled={isLoading.password}
            >
              Click to view
            </Button>
          </div>
        </div>
      </div>

      {auth.auth && <PasskeySettings token={auth.auth.token} />}

      {/* Logout Button */}
      <div className="pt-3 mt-auto">
        <Button onClick={handleLogout} variant="outline" className="w-full justify-center gap-2">
          <LogOut className="w-4 h-4" />
          Logout
        </Button>
      </div>
    </div>
  )
}
